import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { ref, onValue } from 'firebase/database';
import { FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState('next');
  const timeoutRef = useRef();

  // Fetch testimonials from Firebase
  useEffect(() => {
    const testimonialsRef = ref(db, 'testimonials');
    onValue(testimonialsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const testimonialsArray = Object.entries(data).map(([id, testimonial]) => ({ id, ...testimonial }));
        setTestimonials(testimonialsArray);
      }
    });
  }, []);

  // Auto-advance testimonials
  useEffect(() => {
    if (isAutoPlaying && testimonials.length > 0) {
      timeoutRef.current = setTimeout(() => {
        handleNav('next');
      }, 5000);
    }
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line
  }, [isAutoPlaying, testimonials.length, currentIndex]);

  const handleNav = (type) => {
    setDirection(type);
    setCurrentIndex((prev) => {
      return type === 'next'
        ? (prev + 1) % testimonials.length
        : (prev - 1 + testimonials.length) % testimonials.length;
    });
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const goToTestimonial = (idx) => {
    setDirection(idx > currentIndex ? 'next' : 'prev');
    setCurrentIndex(idx);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  // Responsive: 3 cards on desktop, 2 on tablet, 1 on mobile
  let cardsToShow = 3;
  if (window.innerWidth <= 992) cardsToShow = 2;
  if (window.innerWidth <= 576) cardsToShow = 1;
  const visibleTestimonials = [];
  for (let i = 0; i < Math.min(cardsToShow, testimonials.length); i++) {
    const index = (currentIndex + i) % testimonials.length;
    visibleTestimonials.push(testimonials[index]);
  }

  return (
    <div className="testimonials-container">
      <h2 className="testimonials-title">Voices of Experience</h2>
      <p className="testimonials-subtitle">
        Hear what students, parents, and educators say about their transformative journeys with NMT India
      </p>

      {testimonials.length === 0 ? (
        <div className="no-testimonials">
          No testimonials available yet. Check back later!
        </div>
      ) : (
        <div className="testimonial-slider">
          <div className={`testimonial-track track-${direction}`}>
            {visibleTestimonials.map((testimonial, idx) => (
              <div
                key={testimonial.id}
                className={`testimonial-card ${
                  cardsToShow === 1 && idx === 0 ? `animate-card ${direction}` : ''
                }`}
              >
                <FaQuoteLeft className="quote-icon" />
                <img
                  src={testimonial.imageUrl || 'https://via.placeholder.com/100?text=No+Image'}
                  alt={testimonial.name}
                  className="testimonial-image"
                />
                <div className="rating">
                  {'★'.repeat(testimonial.rating || 5)}{'☆'.repeat(5 - (testimonial.rating || 5))}
                </div>
                <h3 className="testimonial-name">{testimonial.name}</h3>
                <p className="testimonial-role">{testimonial.role}</p>
                <p className="testimonial-message">"{testimonial.message}"</p>
              </div>
            ))}
          </div>

          <div className="nav-buttons">
            <button className="nav-button" onClick={() => handleNav('prev')}><FaChevronLeft /></button>
            <button className="nav-button" onClick={() => handleNav('next')}><FaChevronRight /></button>
          </div>

          <div className="dots">
            {testimonials.map((_, index) => (
              <div
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToTestimonial(index)}
              />
            ))}
          </div>
        </div>
      )}

      <style>{`
        .testimonials-container {
          padding: 10px 20px 40px 20px;
          background: linear-gradient(to bottom, #f8f9fa, #e9f5ff);
          text-align: center;
          max-width: 1300px;
          margin: 0 auto;
        }
        .testimonials-title {
          font-size: 2.5rem;
          color: #1669bdff;
          margin-bottom: 15px;
          font-weight: 700;
        }
        .testimonials-subtitle {
          font-size: 1.1rem;
          color: #080808ff;
          max-width: 700px;
          margin: 0 auto 40px;
          line-height: 1.6;
        }
        .no-testimonials {
          text-align: center;
          padding: 40px;
          color: #080808ff;
          font-size: 1.2rem;
        }

        .testimonial-slider {
          position: relative;
          padding: 20px 0;
          overflow: hidden;
        }
        .testimonial-track {
          display: flex;
          gap: 30px;
          justify-content: center;
          align-items: stretch;
        }

        .testimonial-card {
          background: white;
          border-radius: 20px;
          padding: 34px 24px 28px 24px;
          box-shadow: 0 5px 20px rgba(15, 147, 235, 0.11);
          border: 2px solid #2427caff;
          flex: 0 0 calc(33.333% - 20px);
          min-width: 0;
          max-width: 370px;
          margin: 0 auto;
          position: relative;
          transition: box-shadow 0.3s, transform 0.5s cubic-bezier(.54,.06,.5,1.34);
          opacity: 1;
        }
        .testimonial-card:hover {
          transform: translateY(-7px) scale(1.018);
          box-shadow: 0 12px 35px rgba(33,147,176,0.13);
          border-color: #6dd5ed;
        }
        .quote-icon {
          color: #2193b0;
          font-size: 1.6rem;
          margin-bottom: 12px;
        }
        .testimonial-image {
          width: 82px;
          height: 82px;
          border-radius: 50%;
          object-fit: cover;
          margin: 0 auto 16px;
          border: 3px solid #2193b0;
        }
        .rating {
          color: #ffb700;
          font-size: 1.2rem;
          margin: 10px 0;
          letter-spacing: 2px;
        }
        .testimonial-name {
          font-size: 1.25rem;
          color: #0a2326;
          font-weight: 700;
          margin: 10px 0 4px;
        }
        .testimonial-role {
          color: #2193b0;
          font-size: 0.97rem;
          font-weight: 600;
          margin-bottom: 10px;
        }
        .testimonial-message {
          color: #232933;
          line-height: 1.6;
          font-size: 1.04rem;
        }
        .nav-buttons {
          display: flex;
          justify-content: center;
          gap: 18px;
          margin-top: 32px;
        }
        .nav-button {
          background: #196cbeff;
          color: white;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.35rem;
          transition: all 0.25s;
        }
        .nav-button:hover {
          background: #2878d4ff;
          transform: scale(1.12);
        }
        .dots {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 28px;
        }
        .dot {
          width: 13px;
          height: 13px;
          border-radius: 50%;
          background: #b7cfe988;
          cursor: pointer;
          transition: all 0.3s;
        }
        .dot.active {
          background: #2456dfff;
          transform: scale(1.3);
        }

        /* Animations for slide/fade */
        @keyframes slide-next {
          from { opacity: 0; transform: translateX(80px);}
          to   { opacity: 1; transform: translateX(0);}
        }
        @keyframes slide-prev {
          from { opacity: 0; transform: translateX(-80px);}
          to   { opacity: 1; transform: translateX(0);}
        }
        .animate-card.next {
          animation: slide-next 0.65s cubic-bezier(.54,.06,.5,1.34) both;
        }
        .animate-card.prev {
          animation: slide-prev 0.65s cubic-bezier(.54,.06,.5,1.34) both;
        }

        @media (max-width: 992px) {
          .testimonial-card {
            flex: 0 0 calc(50% - 12px);
            padding: 23px 18px 23px 18px;
          }
        }
        @media (max-width: 768px) {
          .testimonials-title {
            font-size: 2rem;
          }
          .testimonials-subtitle {
            font-size: 1rem;
            margin-bottom: 30px;
          }
          .testimonial-track {
            gap: 18px;
          }
          .testimonial-card {
            padding: 18px 10px 18px 10px;
            min-width: 220px;
          }
          .nav-buttons {
            margin-top: 24px;
          }
        }
        @media (max-width: 576px) {
          .testimonials-container {
            padding: 35px 5px;
          }
          .testimonial-track {
            gap: 0px;
          }
          .testimonial-card {
            flex: 0 0 100%;
            margin: 0 5px;
            padding: 13px 7px 15px 7px;
          }
          .nav-buttons {
            margin-top: 14px;
          }
          .nav-button {
            width: 38px;
            height: 38px;
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Testimonials;
